import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Pause play when the tab/window is hidden (Page Visibility API).
 * Timer pauses while away; on return it resumes automatically
 * (Welcome-back overlay disabled).
 *
 * Timer consumers should treat `visibilityPaused` like a hard pause so
 * Date.now()-based elapsed timers stop accumulating while away.
 *
 * @param {{ enabled?: boolean }} [options] — false while puzzle is inactive/solved
 */
export default function usePlayVisibilityPause({ enabled = true } = {}) {
    const [visibilityPaused, setVisibilityPaused] = useState(false);
    const [pauseOverlayOpen, setPauseOverlayOpen] = useState(false);
    const leftWhileEnabledRef = useRef(false);

    useEffect(() => {
        if (!enabled) {
            leftWhileEnabledRef.current = false;
            setVisibilityPaused(false);
            setPauseOverlayOpen(false);
            return undefined;
        }

        const onVisibilityChange = () => {
            if (typeof document === "undefined") return;
            if (document.hidden) {
                leftWhileEnabledRef.current = true;
                setVisibilityPaused(true);
                setPauseOverlayOpen(false);
                return;
            }
            if (leftWhileEnabledRef.current) {
                leftWhileEnabledRef.current = false;
                // Resume on return — Welcome-back popup disabled
                setVisibilityPaused(false);
                setPauseOverlayOpen(false);
                // Previous: keep paused until Continue Playing
                // setVisibilityPaused(true);
                // setPauseOverlayOpen(true);
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [enabled]);

    const continuePlaying = useCallback(() => {
        setPauseOverlayOpen(false);
        setVisibilityPaused(false);
    }, []);

    return {
        /** True while tab is away (timer hard-paused). */
        visibilityPaused,
        /** Welcome-back overlay (disabled — always false). */
        pauseOverlayOpen,
        continuePlaying,
    };
}
